import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import StarRating from '../../components/StarRating';

export default function AdminTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ status: '', priority: '', location_id: '', search: '' });
  const [loading, setLoading] = useState(true);

  const fetchTickets = async (page = 1, f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '10' });
      if (f.status) params.set('status', f.status);
      if (f.priority) params.set('priority', f.priority);
      if (f.location_id) params.set('location_id', f.location_id);
      if (f.search.trim()) params.set('search', f.search.trim());
      const { data } = await api.get(`/tickets?${params}`);
      setTickets(data.tickets || []);
      setPagination(data.pagination || { page: 1, totalPages: 1 });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const lRes = await api.get('/locations/all');
        setLocations(lRes.data.locations || []);
      } catch (e) {
        console.error(e);
      }
      await fetchTickets(1);
    })();
  }, []);

  const applyFilters = () => fetchTickets(1);

  const clearFilters = () => {
    const empty = { status: '', priority: '', location_id: '', search: '' };
    setFilters(empty);
    fetchTickets(1, empty);
  };

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>All Tickets</h1>

      <div className="tr-card" style={{ padding: '1rem', marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 140 }}>
          <label className="tr-label">Status</label>
          <select className="tr-input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div style={{ minWidth: 140 }}>
          <label className="tr-label">Priority</label>
          <select className="tr-input" value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
            <option value="">All</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div style={{ minWidth: 180, flex: '1 1 180px' }}>
          <label className="tr-label">Location</label>
          <select className="tr-input" value={filters.location_id} onChange={(e) => setFilters({ ...filters, location_id: e.target.value })}>
            <option value="">All</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ minWidth: 200, flex: '2 1 200px' }}>
          <label className="tr-label">Search</label>
          <input className="tr-input" placeholder="Subject, visitor, ticket #" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
        </div>
        <button type="button" className="tr-btn tr-btn-primary" onClick={applyFilters}>
          Apply
        </button>
        <button type="button" className="tr-btn tr-btn-outline" onClick={clearFilters}>
          Clear
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="tr-spinner" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="tr-card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          No tickets match your filters.
        </div>
      ) : (
        <>
          <div className="tr-card" style={{ padding: 0, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 720 }}>
              <thead>
                <tr style={{ textAlign: 'left', background: 'var(--bg-muted)' }}>
                  <th style={{ padding: '0.65rem 1rem', whiteSpace: 'nowrap' }}>Ticket No.</th>
                  <th style={{ padding: '0.65rem 1rem' }}>Subject</th>
                  <th style={{ padding: '0.65rem 1rem' }}>Visitor</th>
                  <th style={{ padding: '0.65rem 1rem' }}>Location</th>
                  <th style={{ padding: '0.65rem 1rem' }}>Category</th>
                  <th style={{ padding: '0.65rem 1rem' }}>Priority</th>
                  <th style={{ padding: '0.65rem 1rem' }}>Status</th>
                  <th style={{ padding: '0.65rem 1rem' }}>Rating</th>
                  <th style={{ padding: '0.65rem 1rem' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/admin/tickets/${row.id}`)}
                    style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                  >
                    <td style={{ padding: '0.65rem 1rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{row.ticket_number}</td>
                    <td style={{ padding: '0.65rem 1rem', maxWidth: 220 }}>{row.subject}</td>
                    <td style={{ padding: '0.65rem 1rem' }}>{row.visitor_name}</td>
                    <td style={{ padding: '0.65rem 1rem' }}>{row.location_name}</td>
                    <td style={{ padding: '0.65rem 1rem' }}>{row.category}</td>
                    <td style={{ padding: '0.65rem 1rem' }}>
                      <span className={`badge badge-${row.priority}`}>{row.priority}</span>
                    </td>
                    <td style={{ padding: '0.65rem 1rem' }}>
                      <span className={`badge badge-${row.status}`}>{row.status.replace('_', ' ')}</span>
                    </td>
                    <td style={{ padding: '0.65rem 1rem' }}>
                      <StarRating value={row.rating} readOnly size={14} />
                    </td>
                    <td style={{ padding: '0.65rem 1rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(row.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', marginTop: '1rem', alignItems: 'center' }}>
            <button type="button" className="tr-btn tr-btn-outline" disabled={pagination.page <= 1} onClick={() => fetchTickets(pagination.page - 1)}>
              Previous
            </button>
            <span style={{ fontSize: '0.9rem' }}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button type="button" className="tr-btn tr-btn-outline" disabled={pagination.page >= pagination.totalPages} onClick={() => fetchTickets(pagination.page + 1)}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
